import type { Platform } from "@/context/platform"
import { sendNotificationMessage } from "@/context/notification-worker"
import { navigateNotification } from "./notification-routing"
import { captureNotificationHealth } from "./notification-health"
import { acknowledgePushNotification } from "@/transport/push"
import { withPushRequestTimeout } from "./mobile-push-controller"
import { currentNotificationView } from "./notification-view"
import { suppressSystemNotification } from "@/utils/notification-state"

export const resolveBrowserNotification: NonNullable<Platform["resolveNotification"]> = async (input) => {
  if (!("serviceWorker" in navigator)) return
  const health = captureNotificationHealth()
  const result = await sendNotificationMessage("mux.notification.resolve", {
    ...input, healthOwner: health.owner, tag: `mux-${input.kind}-${input.requestID}`,
  })
  health.record({ id: input.requestID, machineName: input.machineName, operation: "resolve", operationID: result.operationID, outcome: result.outcome ?? "resolved" })
}

export function browserNotificationPermission(): NotificationPermission | "unsupported" {
  return typeof window !== "undefined" && window.isSecureContext && "Notification" in window
    ? Notification.permission : "unsupported"
}

export async function requestBrowserNotificationPermission() {
  if (browserNotificationPermission() === "unsupported") throw new Error("Notifications are unavailable in this browser.")
  const permission = await Notification.requestPermission()
  if (permission !== "granted") throw new Error("Allow notifications for Kettle in your browser's site settings.")
}

async function acknowledgeViewedNotification(payload: { id: string; machineName?: string }) {
  if (!payload.machineName || !("serviceWorker" in navigator) || !("PushManager" in window)) return
  try {
    await withPushRequestTimeout(async (signal) => {
      const registration = await navigator.serviceWorker.getRegistration("/")
      const subscription = await registration?.pushManager.getSubscription()
      if (!subscription || signal.aborted) return
      await acknowledgePushNotification({ endpoint: subscription.endpoint, machineName: payload.machineName!, id: payload.id }, signal)
    }, "registration", 2_000)
  } catch {
    console.warn("Foreground notification acknowledgment failed; a background push may still arrive.")
  }
}

export const notifyBrowser: Platform["notify"] = async (title, description, href, behavior) => {
  if (browserNotificationPermission() !== "granted") return
  const target = new URL(href || location.href, location.origin)
  if (target.origin !== location.origin) throw new Error("Notification destination must belong to Kettle.")
  const parts = target.pathname.split("/").filter(Boolean).map(decodeURIComponent)
  const machineIndex = parts.indexOf("m")
  const kind = behavior?.kind
  const payload = {
    id: behavior?.id || crypto.randomUUID(), title, body: description, href: target.href, kind,
    sessionID: behavior?.sessionID, requestID: behavior?.requestID,
    machineName: machineIndex >= 0 ? parts[machineIndex + 1] : undefined,
    tag: kind ? `mux-${kind}-${behavior?.requestID || behavior?.sessionID}` : undefined,
    suppressWhenViewing: behavior?.suppressWhenViewing,
  }
  const view = currentNotificationView()
  const viewing = view
    ? view.active && !!view.sessionID && view.sessionID === payload.sessionID && (!payload.machineName || view.machineName === payload.machineName)
    : suppressSystemNotification(document.visibilityState, document.hasFocus(), location.href, target.href)
  if (behavior?.alreadyViewed || (behavior?.suppressWhenViewing !== false && viewing)) {
    await acknowledgeViewedNotification(payload)
    return
  }
  if (!("serviceWorker" in navigator)) {
    const notification = new Notification(title, { body: description, icon: "/favicon-v4.svg", data: { href: target.href } })
    notification.onclick = () => {
      navigateNotification(target.href)
      notification.close()
    }
    return
  }
  const health = captureNotificationHealth()
  const result = await sendNotificationMessage("mux.notification.show", { ...payload, healthOwner: health.owner })
  if (result.outcome) health.record({ id: payload.id, machineName: payload.machineName, operation: "show", operationID: result.operationID, outcome: result.outcome })
  if (result.outcome === "viewed") await acknowledgeViewedNotification(payload)
}
